import * as i0 from '@angular/core';
import { EventEmitter, Injectable, Directive, Input, NgModule, Component, VERSION, Inject, Optional, makeStateKey, createNgModule, inject, Injector, PLATFORM_ID } from '@angular/core';
import * as i1 from '@angular/common';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import domAdapter from 'devextreme/core/dom_adapter';
import { one, triggerHandler } from 'devextreme/events';
import config from 'devextreme/core/config';
import { equalByValue } from 'devextreme/core/utils/common';
import render from 'devextreme/core/renderer';
import httpRequest from 'devextreme/core/http_request';
import readyCallbacks from 'devextreme/core/utils/ready_callbacks';
import eventsEngine from 'devextreme/common/core/events/core/events_engine';
import ajax from 'devextreme/core/utils/ajax';
import { Deferred } from 'devextreme/core/utils/deferred';

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
class DxTemplateHost {
  host;
  setHost(host) {
    this.host = host;
  }
  setTemplate(template) {
    this.host.setTemplate(template);
  }
}

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
class NgEventsStrategy {
  instance;
  zone;
  subscriptions = {};
  events = {};
  constructor(instance, zone) {
    this.instance = instance;
    this.zone = zone;
  }
  hasEvent(name) {
    return this.getEmitter(name).observers.length !== 0;
  }
  fireEvent(name, args) {
    const emitter = this.getEmitter(name);
    if (emitter.observers.length) {
      const internalSubs = this.subscriptions[name] || [];
      if (internalSubs.length === emitter.observers.length) {
        emitter.next(args?.[0]);
      } else {
        this.zone.run(() => emitter.next(args?.[0]));
      }
    }
  }
  on(name, handler) {
    if (typeof name === 'string') {
      const eventSubscriptions = this.subscriptions[name] || [];
      const subcription = this.getEmitter(name).subscribe(handler?.bind(this.instance));
      const unsubscribe = subcription.unsubscribe.bind(subcription);
      eventSubscriptions.push({
        handler,
        unsubscribe
      });
      this.subscriptions[name] = eventSubscriptions;
    } else {
      const handlersObj = name;
      Object.keys(handlersObj).forEach(event => this.on(event, handlersObj[event]));
    }
  }
  off(name, handler) {
    const eventSubscriptions = this.subscriptions[name] || [];
    if (handler) {
      eventSubscriptions.some((subscription, i) => {
        if (subscription.handler === handler) {
          subscription.unsubscribe();
          eventSubscriptions.splice(i, 1);
          return true;
        }
        return false;
      });
    } else {
      eventSubscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
      eventSubscriptions.splice(0, eventSubscriptions.length);
    }
  }
  dispose() {}
  addEmitter(eventName, emitter) {
    this.events[eventName] = emitter;
  }
  getEmitter(eventName) {
    if (!this.events[eventName]) {
      this.events[eventName] = new EventEmitter();
    }
    return this.events[eventName];
  }
}
class EmitterHelper {
  zone;
  component;
  lockedValueChangeEvent = false;
  constructor(zone, component) {
    this.zone = zone;
    this.component = component;
  }
  fireNgEvent(eventName, eventArgs) {
    if (this.lockedValueChangeEvent && eventName === 'valueChange') {
      return;
    }
    const emitter = this.component[eventName];
    if (emitter?.observers.length) {
      this.zone.run(() => {
        emitter.next(eventArgs?.[0]);
      });
    }
  }
  createEmitters(events) {
    events.forEach(event => {
      this.component[event.emit] = new EventEmitter();
    });
  }
}

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
let WatcherHelper = /*#__PURE__*/(() => {
  class WatcherHelper {
    _watchers = [];
    getWatchMethod() {
      const watchMethod = (valueGetter, valueChangeCallback, options) => {
        let oldValue = valueGetter();
        options = options || {};
        if (!options.skipImmediate) {
          valueChangeCallback(oldValue);
        }
        const watcher = () => {
          const newValue = valueGetter();
          if (this._isDifferentValues(oldValue, newValue, options.deep)) {
            valueChangeCallback(newValue);
            oldValue = newValue;
          }
        };
        this._watchers.push(watcher);
        return () => {
          const index = this._watchers.indexOf(watcher);
          if (index !== -1) {
            this._watchers.splice(index, 1);
          }
        };
      };
      return watchMethod;
    }
    _isDifferentValues(oldValue, newValue, deepCheck) {
      const comparableNewValue = this._toComparable(newValue);
      const comparableOldValue = this._toComparable(oldValue);
      const isObjectValues = comparableNewValue instanceof Object && comparableOldValue instanceof Object;
      if (deepCheck && isObjectValues) {
        return this._checkObjectsFields(newValue, oldValue);
      }
      return comparableNewValue !== comparableOldValue;
    }
    _toComparable(value) {
      if (value instanceof Date) {
        return value.getTime();
      }
      return value;
    }
    _checkObjectsFields(checkingFromObject, checkingToObject) {
      for (const field in checkingFromObject) {
        const oldValue = this._toComparable(checkingFromObject[field]);
        const newValue = this._toComparable(checkingToObject[field]);
        let isEqualObjects = false;
        if (typeof oldValue === 'object' && typeof newValue === 'object') {
          isEqualObjects = equalByValue(oldValue, newValue);
        }
        if (oldValue !== newValue && !isEqualObjects) {
          return true;
        }
      }
    }
    checkWatchers() {
      for (const watcher of this._watchers) {
        watcher();
      }
    }
    /** @nocollapse */
    static ɵfac = function WatcherHelper_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || WatcherHelper)();
    };
    /** @nocollapse */
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: WatcherHelper,
      factory: WatcherHelper.ɵfac
    });
  }
  return WatcherHelper;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
function getElement(element) {
  return element.get ? element.get(0) : element;
}

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
/* tslint:disable:use-input-property-decorator */
const DX_TEMPLATE_WRAPPER_CLASS = 'dx-template-wrapper';
class RenderData {
  model;
  index;
  container;
}
let DxTemplateDirective = /*#__PURE__*/(() => {
  class DxTemplateDirective {
    templateRef;
    viewContainerRef;
    renderer;
    zone;
    set dxTemplateOf(value) {
      this.name = value;
    }
    name;
    constructor(templateRef, viewContainerRef, templateHost, renderer, zone) {
      this.templateRef = templateRef;
      this.viewContainerRef = viewContainerRef;
      this.renderer = renderer;
      this.zone = zone;
      templateHost.setTemplate(this);
    }
    renderTemplate(renderData) {
      const childView = this.viewContainerRef.createEmbeddedView(this.templateRef, {
        $implicit: renderData.model,
        index: renderData.index
      });
      const container = getElement(renderData.container);
      if (renderData.container) {
        childView.rootNodes.forEach(element => {
          this.renderer.appendChild(container, element);
        });
      }
      return childView;
    }
    render(renderData) {
      let childView;
      if (this.zone.isStable) {
        childView = this.zone.run(() => this.renderTemplate(renderData));
      } else {
        childView = this.renderTemplate(renderData);
      }
      // =========== WORKAROUND =============
      // https://github.com/angular/angular/issues/12243
      childView.detectChanges();
      // =========== /WORKAROUND =============
      childView.rootNodes.forEach(element => {
        if (element.nodeType === 1) {
          domAdapter.setClass(element, DX_TEMPLATE_WRAPPER_CLASS, true);
        }
        one(element, 'dxremove', ({}, params) => {
          if (!params?._angularIntegration) {
            childView.destroy();
          }
        });
      });
      return childView.rootNodes;
    }
    /** @nocollapse */
    static ɵfac = function DxTemplateDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxTemplateDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(DxTemplateHost), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.NgZone));
    };
    /** @nocollapse */
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: DxTemplateDirective,
      selectors: [["", "dxTemplate", ""]],
      inputs: {
        dxTemplateOf: "dxTemplateOf"
      }
    });
  }
  return DxTemplateDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DxTemplateModule = /*#__PURE__*/(() => {
  class DxTemplateModule {
    /** @nocollapse */static ɵfac = function DxTemplateModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxTemplateModule)();
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxTemplateModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return DxTemplateModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
const VISIBILITY_CHANGE_SELECTOR = 'dx-visibility-change-handler';
let BaseNestedOption = /*#__PURE__*/(() => {
  class BaseNestedOption {
    _host;
    _hostOptionPath;
    _collectionContainerImpl;
    _initialOptions = {};
    constructor() {
      this._collectionContainerImpl = new CollectionNestedOptionContainerImpl(this._setOption.bind(this), this._filterItems.bind(this));
    }
    _optionChangedHandler(e) {
      const fullOptionPath = this._fullOptionPath();
      if (e.fullName.indexOf(fullOptionPath) === 0) {
        const optionName = e.fullName.slice(fullOptionPath.length);
        const emitter = this[`${optionName}Change`];
        if (emitter) {
          emitter.next(e.value);
        }
      }
    }
    _createEventEmitters(events) {
      events.forEach(event => {
        this[event.emit] = new EventEmitter();
      });
    }
    _getOption(name) {
      if (this.isLinked) {
        return this.instance.option(this._fullOptionPath() + name);
      }
      return this._initialOptions[name];
    }
    _setOption(name, value) {
      if (this.isLinked) {
        const fullPath = this._fullOptionPath() + name;
        this.instance.option(fullPath, value);
      } else {
        this._initialOptions[name] = value;
      }
    }
    _addRemovedOption(name) {
      if (this.instance && this.removedNestedComponents) {
        this.removedNestedComponents.push(name);
      }
    }
    _deleteRemovedOptions(name) {
      if (this.instance && this.removedNestedComponents) {
        this.removedNestedComponents = this.removedNestedComponents.filter(x => !x.startsWith(name));
      }
    }
    _addRecreatedComponent() {
      if (this.instance && this.recreatedNestedComponents) {
        this.recreatedNestedComponents.push({
          getOptionPath: () => this._getOptionPath()
        });
      }
    }
    _getOptionPath() {
      return this._hostOptionPath() + this._optionPath;
    }
    setHost(host, optionPath) {
      this._host = host;
      this._hostOptionPath = optionPath;
      this.optionChangedHandlers.subscribe(this._optionChangedHandler.bind(this));
    }
    setChildren(propertyName, items) {
      this.resetOptions(propertyName);
      return this._collectionContainerImpl.setChildren(propertyName, items);
    }
    _filterItems(items) {
      return items.filter(item => item !== this);
    }
    get instance() {
      return this._host?.instance;
    }
    get resetOptions() {
      return this._host && this._host.resetOptions;
    }
    get isRecreated() {
      return this._host && this._host.isRecreated;
    }
    get removedNestedComponents() {
      return this._host && this._host.removedNestedComponents;
    }
    set removedNestedComponents(value) {
      this._host.removedNestedComponents = value;
    }
    get recreatedNestedComponents() {
      return this._host && this._host.recreatedNestedComponents;
    }
    set recreatedNestedComponents(value) {
      this._host.recreatedNestedComponents = value;
    }
    get isLinked() {
      return !!this.instance && this._host.isLinked;
    }
    get optionChangedHandlers() {
      return this._host && this._host.optionChangedHandlers;
    }
    /** @nocollapse */
    static ɵfac = function BaseNestedOption_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || BaseNestedOption)();
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: BaseNestedOption,
      selectors: [["ng-component"]],
      standalone: false,
      decls: 0,
      vars: 0,
      template: function BaseNestedOption_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return BaseNestedOption;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class CollectionNestedOptionContainerImpl {
  _setOption;
  _filterItems;
  _activatedQueries = {};
  constructor(_setOption, _filterItems) {
    this._setOption = _setOption;
    this._filterItems = _filterItems;
  }
  setChildren(propertyName, items) {
    if (this._filterItems) {
      items = this._filterItems(items);
    }
    if (items.length) {
      this._activatedQueries[propertyName] = true;
    }
    if (this._activatedQueries[propertyName]) {
      const widgetItems = items.map((item, index) => {
        item._index = index;
        return item._value;
      });
      this._setOption(propertyName, widgetItems);
    }
  }
}
let NestedOption = /*#__PURE__*/(() => {
  class NestedOption extends BaseNestedOption {
    setHost(host, optionPath) {
      super.setHost(host, optionPath);
      this._host[this._optionPath] = this._initialOptions;
    }
    _fullOptionPath() {
      return `${this._getOptionPath()}.`;
    }
    /** @nocollapse */
    static ɵfac = /* @__PURE__ */(() => {
      let ɵNestedOption_BaseFactory;
      return function NestedOption_Factory(__ngFactoryType__) {
        return (ɵNestedOption_BaseFactory || (ɵNestedOption_BaseFactory = i0.ɵɵgetInheritedFactory(NestedOption)))(__ngFactoryType__ || NestedOption);
      };
    })();
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NestedOption,
      selectors: [["ng-component"]],
      standalone: false,
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 0,
      vars: 0,
      template: function NestedOption_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return NestedOption;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CollectionNestedOption = /*#__PURE__*/(() => {
  class CollectionNestedOption extends BaseNestedOption {
    _index;
    _fullOptionPath() {
      return `${this._getOptionPath()}[${this._index}].`;
    }
    get _value() {
      return this._initialOptions;
    }
    get isLinked() {
      return this._index !== undefined && !!this.instance && this._host.isLinked;
    }
    /** @nocollapse */
    static ɵfac = /* @__PURE__ */(() => {
      let ɵCollectionNestedOption_BaseFactory;
      return function CollectionNestedOption_Factory(__ngFactoryType__) {
        return (ɵCollectionNestedOption_BaseFactory || (ɵCollectionNestedOption_BaseFactory = i0.ɵɵgetInheritedFactory(CollectionNestedOption)))(__ngFactoryType__ || CollectionNestedOption);
      };
    })();
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: CollectionNestedOption,
      selectors: [["ng-component"]],
      standalone: false,
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 0,
      vars: 0,
      template: function CollectionNestedOption_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return CollectionNestedOption;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const triggerShownEvent = function (element) {
  const changeHandlers = [];
  if (!render(element).hasClass(VISIBILITY_CHANGE_SELECTOR)) {
    changeHandlers.push(element);
  }
  changeHandlers.push.apply(changeHandlers, element.querySelectorAll(`.${VISIBILITY_CHANGE_SELECTOR}`));
  for (let i = 0; i < changeHandlers.length; i++) {
    triggerHandler(changeHandlers[i], 'dxshown');
  }
};
function extractTemplate(option, element, renderer, document) {
  if (!option.template === undefined || !element.nativeElement.hasChildNodes()) {
    return;
  }
  const childNodes = [].slice.call(element.nativeElement.childNodes);
  const userContent = childNodes.filter(n => {
    if (n.tagName) {
      const tagNamePrefix = n.tagName.toLowerCase().substr(0, 3);
      return !(tagNamePrefix === 'dxi' || tagNamePrefix === 'dxo');
    }
    return n.nodeName !== '#comment' && n.textContent.replace(/\s/g, '').length;
  });
  if (!userContent.length) {
    return;
  }
  option.template = {
    render: renderData => {
      const result = element.nativeElement;
      domAdapter.setClass(result, DX_TEMPLATE_WRAPPER_CLASS, true);
      if (renderData.container) {
        const container = getElement(renderData.container);
        const resultInContainer = container.contains(element.nativeElement);
        renderer.appendChild(container, element.nativeElement);
        if (!resultInContainer) {
          const resultInBody = document.body.contains(container);
          if (resultInBody) {
            triggerShownEvent(result);
          }
        }
      }
      return result;
    }
  };
}
class NestedOptionHost {
  _host;
  _optionPath;
  getHost() {
    return this._host;
  }
  setHost(host, optionPath) {
    this._host = host;
    this._optionPath = optionPath || (() => '');
  }
  setNestedOption(nestedOption) {
    nestedOption.setHost(this._host, this._optionPath);
  }
}

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
/* tslint:disable:max-line-length */
const outsideZoneEvents = ['mousemove', 'mouseover', 'mouseout'];
const insideZoneEvents = ['mouseup', 'click', 'mousedown', 'transitionend', 'wheel'];
let originalAdd;
let callbacks = [];
let readyCallbackAdd = function (callback) {
  if (!originalAdd) {
    originalAdd = this.callBase.bind(this);
  }
  callbacks.push(callback);
};
readyCallbacks.inject({
  add(callback) {
    return readyCallbackAdd.call(this, callback);
  }
});
let doInjections = (document, ngZone, xhrFactory) => {
  if (Number(VERSION.major) < 12) {
    console.warn('Your version of Angular is not supported. Please update your project to version 12 or later.' + ' Please refer to the Angular Update Guide for more information: https://update.angular.io');
  }
  domAdapter.inject({
    _document: document,
    listen(...args) {
      const eventName = args[1];
      if (outsideZoneEvents.includes(eventName)) {
        return ngZone.runOutsideAngular(() => this.callBase.apply(this, args));
      }
      if (ngZone.isStable && insideZoneEvents.includes(eventName)) {
        return ngZone.run(() => this.callBase.apply(this, args));
      }
      return this.callBase.apply(this, args);
    },
    isElementNode(element) {
      return element && element.nodeType === 1;
    },
    isTextNode(element) {
      return element && element.nodeType === 3;
    },
    isDocument(element) {
      return element && element.nodeType === 9;
    }
  });
  httpRequest.inject({
    getXhr() {
      if (!xhrFactory) {
        return this.callBase.apply(this);
      }
      const _xhr = xhrFactory.build();
      if (!('withCredentials' in _xhr)) {
        _xhr.withCredentials = false;
      }
      return _xhr;
    }
  });
  const runReadyCallbacksInZone = () => {
    ngZone.run(() => {
      eventsEngine.set({});
      callbacks.forEach(callback => originalAdd.call(null, callback));
      callbacks = [];
      readyCallbacks.fire();
    });
  };
  runReadyCallbacksInZone();
  readyCallbackAdd = callback => ngZone.run(() => callback());
  doInjections = runReadyCallbacksInZone;
};
let DxIntegrationModule = /*#__PURE__*/(() => {
  class DxIntegrationModule {
    static initialized = false;
    constructor(document, ngZone, xhrFactory) {
      doInjections(document, ngZone, xhrFactory);
      DxIntegrationModule.initialized = true;
    }
    /** @nocollapse */
    static ɵfac = function DxIntegrationModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxIntegrationModule)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(i1.XhrFactory, 8));
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxIntegrationModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return DxIntegrationModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
config({
  buyNowLink: 'https://go.devexpress.com/Licensing_Installer_Watermark_DevExtremeAngular.aspx',
  licensingDocLink: 'https://go.devexpress.com/Licensing_Documentation_DevExtremeAngular.aspx'
});
let serverStateKey;
const getServerStateKey = () => {
  if (!serverStateKey) {
    serverStateKey = makeStateKey('DX_isPlatformServer');
  }
  return serverStateKey;
};
let DxComponent = /*#__PURE__*/(() => {
  class DxComponent {
    element;
    ngZone;
    watcherHelper;
    transferState;
    platformId;
    _initialOptions = {};
    _optionsToUpdate = {};
    _collectionContainerImpl;
    eventHelper;
    optionChangedHandlers = new EventEmitter();
    templates;
    instance;
    isLinked = true;
    changedOptions = {};
    removedNestedComponents = [];
    recreatedNestedComponents;
    widgetUpdateLocked = false;
    templateUpdateRequired = false;
    _updateTemplates() {
      if (this.templates.length && this.templateUpdateRequired) {
        const updatedTemplates = {};
        this.templates.forEach(template => {
          updatedTemplates[template.name] = template;
        });
        this.instance.option('integrationOptions.templates', updatedTemplates);
        this.templates = Object.values(updatedTemplates);
        this.templateUpdateRequired = false;
      }
    }
    _initEvents() {
      this.instance.on('optionChanged', e => {
        this.changedOptions[e.name] = e.value;
        const value = e.name === e.fullName ? e.value : e.component.option(e.name);
        this.eventHelper.fireNgEvent(`${e.name}Change`, [value]);
        this.optionChangedHandlers.emit(e);
      });
    }
    _initOptions() {
      this._initialOptions.integrationOptions.watchMethod = this.watcherHelper.getWatchMethod();
    }
    _initPlatform() {
      if (this.transferState.hasKey(getServerStateKey())) {
        this._initialOptions.integrationOptions.renderedOnServer = this.transferState.get(getServerStateKey(), null);
      } else if (isPlatformServer(this.platformId)) {
        this.transferState.set(getServerStateKey(), true);
      }
    }
    _createEventEmitters(events) {
      const zone = this.ngZone;
      this.eventHelper.createEmitters(events);
      this._initialOptions.eventsStrategy = instance => {
        const strategy = new NgEventsStrategy(instance, zone);
        events.filter(event => event.subscribe).forEach(event => {
          strategy.addEmitter(event.subscribe, this[event.emit]);
        });
        return strategy;
      };
      this._initialOptions.nestedComponentOptions = function (component) {
        return {
          eventsStrategy: instance => new NgEventsStrategy(instance, zone),
          nestedComponentOptions: component.option('nestedComponentOptions')
        };
      };
    }
    _shouldOptionChange(name, value) {
      if (this.changedOptions.hasOwnProperty(name)) {
        const prevValue = this.changedOptions[name];
        delete this.changedOptions[name];
        return value !== prevValue;
      }
      return true;
    }
    clearChangedOptions() {
      this.changedOptions = {};
    }
    _getOption(name) {
      return this.instance ? this.instance.option(name) : this._initialOptions[name];
    }
    lockWidgetUpdate() {
      if (!this.widgetUpdateLocked && this.instance) {
        this.instance.beginUpdate();
        this.widgetUpdateLocked = true;
      }
    }
    unlockWidgetUpdate() {
      if (this.widgetUpdateLocked) {
        this.widgetUpdateLocked = false;
        this.instance.endUpdate();
      }
    }
    _setOption(name, value) {
      this.lockWidgetUpdate();
      if (!this._shouldOptionChange(name, value)) {
        return;
      }
      if (this.instance) {
        this.instance.option(name, value);
      } else {
        this._initialOptions[name] = value;
      }
    }
    _createWidget(element) {
      this._initialOptions.integrationOptions = {};
      this._initPlatform();
      this._initOptions();
      this._initialOptions.onInitializing = function () {
        this.beginUpdate();
      };
      this.instance = this._createInstance(element, this._initialOptions);
      this._initEvents();
      this._initialOptions = {};
    }
    _destroyWidget() {
      this.removedNestedComponents = [];
      if (this.instance) {
        const element = this.instance.element();
        triggerHandler(element, 'dxremove', {
          _angularIntegration: true
        });
        this.instance.dispose();
        domAdapter.removeElement(element);
      }
    }
    _setChildren(propertyName, value, className) {
      if (this.checkContentChildren(propertyName, value, className)) {
        this.setContentChildren(propertyName, value, className);
        this.setChildren(propertyName, value);
      }
    }
    constructor(element, ngZone, templateHost, watcherHelper, transferState, platformId) {
      this.element = element;
      this.ngZone = ngZone;
      this.watcherHelper = watcherHelper;
      this.transferState = transferState;
      this.platformId = platformId;
      if (!DxIntegrationModule.initialized) {
        createNgModule(DxIntegrationModule, inject(Injector));
      }
      this.templates = [];
      templateHost.setHost(this);
      this._collectionContainerImpl = new CollectionNestedOptionContainerImpl(this._setOption.bind(this));
      this.eventHelper = new EmitterHelper(ngZone, this);
    }
    ngOnChanges(changes) {
      for (const key in changes) {
        const change = changes[key];
        if (change.currentValue !== this[key]) {
          this._optionsToUpdate[key] = changes[key].currentValue;
        }
      }
    }
    ngOnInit() {
      this._createWidget(this.element.nativeElement);
    }
    ngDoCheck() {
      this.applyOptions();
    }
    ngAfterContentChecked() {
      this.applyOptions();
      this.resetOptions();
      this.unlockWidgetUpdate();
    }
    ngAfterViewInit() {
      this._updateTemplates();
      this.instance.endUpdate();
      this.recreatedNestedComponents = [];
    }
    ngAfterViewChecked() {
      this._updateTemplates();
    }
    applyOptions() {
      if (Object.keys(this._optionsToUpdate).length) {
        if (this.instance) {
          this.instance.option(this._optionsToUpdate);
        }
        this._optionsToUpdate = {};
      }
    }
    resetOptions(collectionName) {
      if (this.instance) {
        this.removedNestedComponents.filter(option => option && !this.isRecreated(option) && collectionName ? option.startsWith(collectionName) : true).forEach(option => {
          this.instance.resetOption(option);
        });
        this.removedNestedComponents = [];
        this.recreatedNestedComponents = [];
      }
    }
    isRecreated(name) {
      return this.recreatedNestedComponents && this.recreatedNestedComponents.some(nestedComponent => nestedComponent.getOptionPath() === name);
    }
    setTemplate(template) {
      this.templates.push(template);
      this.templateUpdateRequired = true;
    }
    contentChildren = {};
    checkContentChildren(propertyName, items, className) {
      if (this.contentChildren[propertyName] && this.contentChildren[propertyName] !== className) {
        if (items.length > 0) {
          if (console && console.warn) {
            console.warn(`In ${this.constructor.name}, 
          the nested ${className} and ${this.contentChildren[propertyName]} components are incompatible. 
          Ensure that all nested components in the content area match.`);
          }
        }
        return false;
      }
      return true;
    }
    setContentChildren(propertyName, items, className) {
      if (items.length > 0) {
        this.contentChildren[propertyName] = className;
      }
    }
    setChildren(propertyName, items) {
      this.resetOptions(propertyName);
      return this._collectionContainerImpl.setChildren(propertyName, items);
    }
    /** @nocollapse */
    static ɵfac = function DxComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(DxTemplateHost), i0.ɵɵdirectiveInject(WatcherHelper), i0.ɵɵdirectiveInject(i0.TransferState), i0.ɵɵdirectiveInject(PLATFORM_ID));
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DxComponent,
      selectors: [["ng-component"]],
      standalone: false,
      features: [i0.ɵɵNgOnChangesFeature],
      decls: 0,
      vars: 0,
      template: function DxComponent_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return DxComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DxComponentExtension = /*#__PURE__*/(() => {
  class DxComponentExtension extends DxComponent {
    createInstance(element) {
      this._createWidget(element);
    }
    ngOnInit() {}
    ngAfterViewInit() {
      this._createWidget(this.element.nativeElement);
      this.instance.endUpdate();
    }
    /** @nocollapse */
    static ɵfac = /* @__PURE__ */(() => {
      let ɵDxComponentExtension_BaseFactory;
      return function DxComponentExtension_Factory(__ngFactoryType__) {
        return (ɵDxComponentExtension_BaseFactory || (ɵDxComponentExtension_BaseFactory = i0.ɵɵgetInheritedFactory(DxComponentExtension)))(__ngFactoryType__ || DxComponentExtension);
      };
    })();
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DxComponentExtension,
      selectors: [["ng-component"]],
      standalone: false,
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 0,
      vars: 0,
      template: function DxComponentExtension_Template(rf, ctx) {},
      encapsulation: 2
    });
  }
  return DxComponentExtension;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
function isIterable(value) {
  return value && typeof value[Symbol.iterator] === 'function';
}
let IterableDifferHelper = /*#__PURE__*/(() => {
  class IterableDifferHelper {
    _differs;
    _host;
    _propertyDiffers = {};
    constructor(_differs) {
      this._differs = _differs;
    }
    setHost(host) {
      this._host = host;
    }
    setup(prop, changes) {
      if (prop in changes) {
        const value = changes[prop].currentValue;
        this.setupSingle(prop, value);
      }
    }
    setupSingle(prop, value) {
      if (value && Array.isArray(value)) {
        if (!this._propertyDiffers[prop]) {
          try {
            this._propertyDiffers[prop] = this._differs.find(value).create(null);
            return true;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (e) {}
        }
      } else {
        delete this._propertyDiffers[prop];
      }
      return false;
    }
    getChanges(prop, value) {
      if (this._propertyDiffers[prop]) {
        return this._propertyDiffers[prop].diff(value);
      }
    }
    checkChangedOptions(propName, hostValue) {
      return this._host.changedOptions[propName] === hostValue;
    }
    doCheck(prop) {
      if (this._propertyDiffers[prop] && this._host.instance) {
        const hostValue = this._host[prop];
        const changes = isIterable(hostValue) && this.getChanges(prop, hostValue);
        if (changes && !this.checkChangedOptions(prop, hostValue)) {
          this._host.lockWidgetUpdate();
          this._host.instance.option(prop, hostValue);
        }
      }
    }
    /** @nocollapse */
    static ɵfac = function IterableDifferHelper_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || IterableDifferHelper)(i0.ɵɵinject(i0.IterableDiffers));
    };
    /** @nocollapse */
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: IterableDifferHelper,
      factory: IterableDifferHelper.ɵfac
    });
  }
  return IterableDifferHelper;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
let DxServerTransferStateModule = /*#__PURE__*/(() => {
  class DxServerTransferStateModule {
    state;
    platformId;
    constructor(state, platformId) {
      this.state = state;
      this.platformId = platformId;
      const that = this;
      ajax.inject({
        sendRequest(...args) {
          const key = makeStateKey(that.generateKey(args));
          const cachedData = that.state.get(key, null);
          if (isPlatformServer(that.platformId)) {
            const result = this.callBase.apply(this, args);
            result.always((data, status) => {
              const dataForCache = {
                data,
                status
              };
              that.state.set(key, dataForCache);
            });
            return result;
          }
          if (cachedData) {
            const d = Deferred();
            d.resolve(cachedData.data, cachedData.status);
            that.state.set(key, null);
            return d.promise();
          }
          return this.callBase.apply(this, args);
        }
      });
    }
    generateKey(args) {
      let keyValue = '';
      for (const key in args) {
        if (typeof args[key] === 'object') {
          const objKey = this.generateKey(args[key]);
          keyValue += key + objKey;
        } else {
          keyValue += key + args[key];
        }
      }
      return keyValue;
    }
    /** @nocollapse */
    static ɵfac = function DxServerTransferStateModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxServerTransferStateModule)(i0.ɵɵinject(i0.TransferState), i0.ɵɵinject(PLATFORM_ID));
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxServerTransferStateModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
  return DxServerTransferStateModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BaseNestedOption, CollectionNestedOption, CollectionNestedOptionContainerImpl, DX_TEMPLATE_WRAPPER_CLASS, DxComponent, DxComponentExtension, DxIntegrationModule, DxServerTransferStateModule, DxTemplateDirective, DxTemplateHost, DxTemplateModule, EmitterHelper, IterableDifferHelper, NestedOption, NestedOptionHost, NgEventsStrategy, RenderData, WatcherHelper, extractTemplate, getElement, getServerStateKey };
